import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import TravelSafetyMap from './components/Map';
import TouchOptimizedMap from './components/map/TouchOptimizedMap';
import AdminZoneDrawer from './components/AdminZoneDrawer';
import AuthModal from './components/auth/AuthModal';
import ProtectedRoute from './components/auth/ProtectedRoute';
import UserProfile from './components/auth/UserProfile';
import UserDashboard from './components/UserDashboard';
import VerificationCenter from './components/verification/VerificationCenter';
import AdminDashboard from './components/admin/AdminDashboard';
import ProfileManagement from './components/profile/ProfileManagement';
import ReviewHistory from './components/profile/ReviewHistory';
import AccountSettings from './components/profile/AccountSettings';
import Dashboard from './components/dashboard/Dashboard';
import ResponsiveHeader from './components/navigation/ResponsiveHeader';
import PWAInstaller from './components/pwa/PWAInstaller';
import AlertNotifications from './components/alerts/AlertNotifications';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import CookieConsent from './components/CookieConsent';
import PricingPage from './components/pricing/PricingPage';
import SubscriptionManager from './components/pricing/SubscriptionManager';
import { useAuth } from './contexts/AuthContext';
import { useBreakpoint } from './utils/breakpoints';
import { setupPerformanceMonitoring } from './utils/performance';
import { useGeolocation } from './hooks/useGeolocation';
import './App.css';
import './styles/design-system.css';
import './styles/mobile.css';
import './styles/landing-page.css';

const AppContent = () => {
  const [zones, setZones] = useState([]);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showUserDashboard, setShowUserDashboard] = useState(false);
  const [showVerificationCenter, setShowVerificationCenter] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showProfileManagement, setShowProfileManagement] = useState(false);
  const [showReviewHistory, setShowReviewHistory] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showSocialDashboard, setShowSocialDashboard] = useState(true); // Start with social dashboard as main view
  const [showMapView, setShowMapView] = useState(false);
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const routeLocation = useLocation();

  // Enable automatic location tracking and safety zone subscriptions
  const { location: geoLocation, error: locationError, isTracking } = useGeolocation({
    enabled: isAuthenticated, // Only track when logged in
    radius: 5000, // 5km alert radius
    updateInterval: 60000 // Update every minute
  });

  // Debug auth state (remove in production)
  console.log('🚀 APP: Auth state:', { isAuthenticated, isLoading, user: user?.username });
  console.log('📍 APP: Location tracking:', { isTracking, location: geoLocation, locationError });
  const { isMobile, isTablet } = useBreakpoint();

  useEffect(() => {
    // Debug page load
    console.log('🚀 APP: App component mounted/reloaded');
    
    // Setup performance monitoring
    setupPerformanceMonitoring();
    
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered:', registration);
        })
        .catch((error) => {
          console.log('SW registration failed:', error);
        });
    }
  }, []);

  const handleZonesSaved = (updatedZones) => {
    setZones(updatedZones);
  };

  const handleMapClick = (data) => {
    console.log('Map clicked:', data);
  };

  const handleAuthAction = () => {
    if (isAuthenticated) {
      logout();
    } else {
      setShowAuthModal(true);
    }
  };

  const handleAdminToggle = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setIsAdminMode(!isAdminMode);
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #2196F3',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Show landing page for unauthenticated users (except pricing page and legal pages)
  // Allow pricing page and legal pages to be viewed by anyone
  const publicPaths = ['/pricing', '/privacy', '/terms'];
  const isPublicPath = publicPaths.includes(routeLocation.pathname);

  if (!isAuthenticated && !isPublicPath) {
    return (
      <>
        <LandingPage />
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultMode="login"
        />
      </>
    );
  }

  return (
    <div className="App min-h-screen bg-background">
      <AlertNotifications />

      <ResponsiveHeader
        onShowAuth={() => setShowAuthModal(true)}
        onShowProfile={() => setShowProfileManagement(true)}
        onShowReviews={() => setShowReviewHistory(true)}
        onShowSettings={() => setShowAccountSettings(true)}
        onShowVerification={() => setShowVerificationCenter(true)}
        onShowAdmin={() => setShowAdminDashboard(true)}
        onToggleAdminMode={handleAdminToggle}
        onShowDashboard={() => {
          setShowSocialDashboard(true);
          setShowMapView(false);
        }}
        onShowMap={() => {
          setShowMapView(true);
          setShowSocialDashboard(false);
        }}
        isAdminMode={isAdminMode}
        isDashboardActive={showSocialDashboard}
        isMapActive={showMapView}
      />

      <main className="flex-1">
        {isAdminMode ? (
          <ProtectedRoute requireAuth={true} allowedRoles={['admin']}>
            <AdminZoneDrawer
              existingZones={zones}
              onZonesSaved={handleZonesSaved}
              center={[40.7128, -74.0060]}
              zoom={10}
            />
          </ProtectedRoute>
        ) : showSocialDashboard ? (
          <Dashboard
            isOpen={true}
            onClose={() => setShowSocialDashboard(false)}
            onShowMap={() => {
              setShowMapView(true);
              setShowSocialDashboard(false);
            }}
          />
        ) : (
          <div className="relative" style={{ height: 'calc(100vh - 72px)' }}>
            {isMobile || isTablet ? (
              <TouchOptimizedMap
                onMapClick={handleMapClick}
                safetyZones={zones}
                center={[40.7128, -74.0060]}
                zoom={3}
              />
            ) : (
              <TravelSafetyMap
                onMapClick={handleMapClick}
                safetyZones={zones}
                center={[40.7128, -74.0060]}
                zoom={3}
              />
            )}
          </div>
        )}
      </main>

      <PWAInstaller />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="login"
      />
      
      <UserProfile
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
      />
      
      <UserDashboard
        isOpen={showUserDashboard}
        onClose={() => setShowUserDashboard(false)}
      />
      
      <VerificationCenter
        isOpen={showVerificationCenter}
        onClose={() => setShowVerificationCenter(false)}
      />
      
      <AdminDashboard
        isOpen={showAdminDashboard}
        onClose={() => setShowAdminDashboard(false)}
      />
      
      <ProfileManagement
        isOpen={showProfileManagement}
        onClose={() => setShowProfileManagement(false)}
      />
      
      <ReviewHistory
        isOpen={showReviewHistory}
        onClose={() => setShowReviewHistory(false)}
      />
      
      <AccountSettings
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
      />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/account/subscription" element={<SubscriptionManager />} />
          <Route path="*" element={<AppContent />} />
        </Routes>
        <CookieConsent />
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;